import React, { useState, useEffect, useRef } from 'react'
import {
  Button,
  Icon,
  message,
  Upload,
  Tooltip,
  Spin,
  Card,
  Progress,
  Alert,
  Avatar,
  Modal
} from 'antd'
import {
  listAIVoices,
  uploadCustomVoice,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import StepTitle from '../StepTitle'
import './Voiceover.scss'

const AUDIO_PREVIEW_BASE_URL = 'https://video-generate.sharelookapp.link/audio/'

const Voiceover = ({
  formData,
  setFormData,
  onValidationChange,
  voices,
  setVoices,
  onVoiceChange,
  isClusteringAudio,
  audioClusteringStatus,
  audioClusteringProgress,
  isDraftMode = false
}) => {
  const [loadingVoices, setLoadingVoices] = useState(false)
  const [playingVoice, setPlayingVoice] = useState(null)
  const [showUpload, setShowUpload] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingVoiceId, setPendingVoiceId] = useState(null)
  const audioRef = useRef(null)

  const [audioProgress, setAudioProgress] = useState(0)

  // Get selected voice from formData
  const selectedVoice = formData?.selectedVoice || null

  useEffect(() => {
    if (voices.length === 0) {
      fetchVoices()
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  // Clear error when closing upload panel
  useEffect(() => {
    if (!showUpload) {
      setUploadError(null)
    }
  }, [showUpload])

  // Validate step when voice is selected
  const onValidationChangeRef = useRef(onValidationChange)

  useEffect(() => {
    onValidationChangeRef.current = onValidationChange
  }, [onValidationChange])

  // Validate step when voice is selected
  useEffect(() => {
    if (onValidationChangeRef.current) {
      onValidationChangeRef.current(true)
    }
  }, [])

  const waitForTaskSuccess = async taskId => {
    if (!taskId) throw new Error('Missing task id')

    const maxAttempts = 100
    const delayMs = 3000

    await new Promise(resolve => setTimeout(resolve, 2000))

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const statusResponse = await getTaskStatus(taskId)
        const data = statusResponse?.data || null
        const status = data?.status || null

        if (status === 'success') {
          const output = data?.output || data?.audio_url || data?.result || null
          if (output?.success === false || output?.error) {
            const errorMsg = output?.error || 'Operation failed'
            throw new Error(errorMsg)
          }
          return output
        }

        if (status === 'failed' || status === 'error') {
          const errorMsg = data?.output?.error || data?.error || 'Task failed'
          throw new Error(errorMsg)
        }
      } catch (err) {
        if (err.message === 'Task failed' || attempt === maxAttempts - 1)
          throw err
      }

      await new Promise(resolve => setTimeout(resolve, delayMs))
    }

    throw new Error('Task polling timed out')
  }

  const fetchVoices = async () => {
    setLoadingVoices(true)
    try {
      const res = await listAIVoices()
      const task_id = res?.data?.task_id

      if (!task_id) {
        console.error('No task_id received from listAIVoices')
        setVoices([])
        return
      }

      const data = await waitForTaskSuccess(task_id)

      if (!data) {
        console.error('No data received from task')
        setVoices([])
        return
      }

      const defaultVoices = data?.default_voices || []
      const uploadedVoices = data?.uploaded_voices || []

      const allVoices = [
        ...defaultVoices.map((voice, index) => ({
          id: voice,
          name: `Voice ${index + 1}`,
          type: 'default'
        })),
        ...uploadedVoices.map(voice => ({
          id: voice,
          name: voice,
          type: 'uploaded'
        }))
      ]

      setVoices(allVoices)
    } catch (err) {
      console.error('Failed to fetch voices', err)
      message.error('Failed to load voices')
      setVoices([])
    } finally {
      setLoadingVoices(false)
    }
  }

  const handleSelectVoice = voiceId => {
    if (isClusteringAudio) {
      Modal.info({
        title: 'Audio Generation in Progress',
        content:
          'Audio generation is currently in progress for your scenes. Please wait for it to complete before changing the voice.',
        centered: true
      })
      return
    }

    // // If deselecting the current voice
    // if (selectedVoice === voiceId) {
    //   setFormData(prev => ({
    //     ...prev,
    //     selectedVoice: null
    //   }))
    //   return
    // }

    // Check if we should show confirmation modal
    // On voice card selection, it should not show a confirmation modal if in shotlist, if every scene doesnt have an audio_link
    // if any one has an audio_link then only it should show confirmation modal to override or clone...
    const hasAnyAudioLink = formData?.shotList?.some(
      scene => !!scene.audio_link
    )

    if (isDraftMode || !hasAnyAudioLink) {
      applyVoiceSelection(voiceId)
    } else {
      setPendingVoiceId(voiceId)
      setShowConfirmationModal(true)
    }
  }

  const handleClone = () => {
    Modal.confirm({
      title: 'Confirm Clone',
      content:
        'Are you sure you want to create a new copy of this video with the new voice?',
      centered: true,
      onOk: () => {
        if (onVoiceChange && pendingVoiceId) {
          onVoiceChange(pendingVoiceId, true) // true = clone
        }
        setShowConfirmationModal(false)
        setPendingVoiceId(null)
      }
    })
  }

  const handleOverride = () => {
    Modal.confirm({
      title: 'Confirm Override',
      content:
        'This will regenerate audio for all scenes. Are you sure you want to continue?',
      centered: true,
      okType: 'danger',
      onOk: () => {
        if (pendingVoiceId) {
          applyVoiceSelection(pendingVoiceId)
        }
        setShowConfirmationModal(false)
        setPendingVoiceId(null)
      }
    })
  }

  const handleCancelChange = () => {
    setShowConfirmationModal(false)
    setPendingVoiceId(null)
  }

  const getPendingVoiceName = () => {
    const voice = voices.find(v => v.id === pendingVoiceId)
    return voice?.name || pendingVoiceId
  }

  const applyVoiceSelection = voiceId => {
    if (onVoiceChange) {
      onVoiceChange(voiceId)
    } else {
      setFormData(prev => ({
        ...prev,
        selectedVoice: voiceId,
        voiceoverAudio: null,
        shotList: (prev.shotList || []).map(scene => ({
          ...scene,
          shots: scene.shots.map(shot => ({
            ...shot,
            audio_duration: null,
            audio_link: null
          }))
        }))
      }))
    }
  }

  const handlePlayPreview = voiceId => {
    if (playingVoice === voiceId) {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setPlayingVoice(null)
      setAudioProgress(0)
    } else {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.ontimeupdate = null
      }
      setAudioProgress(0)
      const audio = new Audio(`${AUDIO_PREVIEW_BASE_URL}${voiceId}`)

      audio.ontimeupdate = () => {
        if (audio.duration) {
          const progress = (audio.currentTime / audio.duration) * 100
          setAudioProgress(progress)
        }
      }

      audio.play().catch(err => {
        console.error('Audio preview failed:', err)
        message.error('Failed to play audio preview')
      })

      audio.onended = () => {
        setPlayingVoice(null)
        setAudioProgress(0)
      }

      audioRef.current = audio
      setPlayingVoice(voiceId)
      setAudioProgress(0)
    }
  }

  const handleUploadVoice = async options => {
    const { file, onSuccess, onError } = options

    const uploadFormData = new FormData()
    uploadFormData.append('voice_file', file)
    uploadFormData.append('voice_name', file.name.replace(/\.[^/.]+$/, ''))

    setUploading(true)
    setUploadProgress(0)
    setUploadError(null)

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90))
      }, 200)

      await uploadCustomVoice(uploadFormData)

      clearInterval(progressInterval)
      setUploadProgress(100)

      message.success('Voice uploaded successfully!')
      setShowUpload(false)

      // Refresh voice list
      await fetchVoices()

      if (onSuccess) onSuccess()
    } catch (err) {
      console.error('Upload failed:', err)
      let errorMsg = 'Failed to upload voice'

      if (err.response) {
        if (err.response.status === 413) {
          errorMsg =
            'File is too large. Please upload a file smaller than 50MB.'
        } else if (err.response.data && err.response.data.error) {
          errorMsg = err.response.data.error
        }
      } else if (err.message) {
        errorMsg = err.message
      }

      setUploadError(errorMsg)

      if (onError) onError(err)
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  // Pre-upload validation
  const beforeUpload = file => {
    setUploadError(null)
    const isLt50M = file.size / 1024 / 1024 < 50
    if (!isLt50M) {
      setUploadError('File must be smaller than 50MB!')
      return false
    }
    return true
  }

  const getSelectedVoiceName = () => {
    if (!selectedVoice) return null
    const voice = voices.find(v => v.id === selectedVoice)
    return voice?.name || selectedVoice
  }

  // Check if we have any video content generated
  const hasGeneratedVideo =
    formData.shotList &&
    formData.shotList.some(scene =>
      scene.shots.some(shot => shot.vidSrc || shot.video_duration)
    )

  return (
    <div className="voiceover-step">
      {!isDraftMode && (
        <StepTitle
          iconType="sound"
          title="Voiceover"
          description="Select a voice for your video narration."
        />
      )}

      <div className="voiceover-step__content">
        {isClusteringAudio && (
          <Alert
            message="Generating Audio for all scenes..."
            description={
              <div style={{ marginTop: 8 }}>
                {typeof audioClusteringProgress === 'number' && (
                  <Progress
                    percent={Math.round(audioClusteringProgress)}
                    size="small"
                    status="active"
                  />
                )}
              </div>
            }
            type="info"
            showIcon
            icon={<Icon type="loading" />}
            style={{ marginBottom: 24 }}
          />
        )}
        <div className="voiceover-step__main-layout">
          {/* Section: Custom Voices (including Upload) */}
          <div className="voiceover-step__section">
            <div className="voiceover-step__section-header">
              <h3 className="voiceover-step__section-title">Your Voices</h3>
            </div>
            <div className="voiceover-step__voice-grid">
              {/* Upload Card */}
              <div
                className={`voiceover-step__upload-card ${
                  showUpload ? 'voiceover-step__upload-card--active' : ''
                } ${uploading ? 'voiceover-step__upload-card--disabled' : ''}`}
                onClick={() => !uploading && setShowUpload(!showUpload)}
              >
                <div className="voiceover-step__upload-card-content">
                  <div className="upload-icon-wrapper">
                    <Icon type={showUpload ? 'minus' : 'plus'} />
                  </div>
                  <div className="upload-text-content">
                    <span className="main-text">Add Custom Voice</span>
                    <span className="sub-text">Upload your own audio</span>
                  </div>
                </div>
              </div>

              {voices
                .filter(v => v.type === 'uploaded')
                .map((voice, index) => (
                  <VoiceCard
                    key={voice.id}
                    voice={voice}
                    isSelected={selectedVoice === voice.id}
                    isPlaying={playingVoice === voice.id}
                    progress={playingVoice === voice.id ? audioProgress : 0}
                    onSelect={() => handleSelectVoice(voice.id)}
                    onPlay={e => {
                      e.stopPropagation()
                      handlePlayPreview(voice.id)
                    }}
                  />
                ))}
            </div>

            {/* Inline Upload Panel */}
            {showUpload && (
              <div className="voiceover-step__upload-panel">
                {uploadError && (
                  <Alert
                    message={uploadError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setUploadError(null)}
                    style={{ marginBottom: 16 }}
                  />
                )}
                {uploading ? (
                  <div className="voiceover-step__upload-loading">
                    <Spin
                      indicator={
                        <Icon type="loading" style={{ fontSize: 24 }} spin />
                      }
                      tip="Uploading voice..."
                    />
                    <Progress percent={uploadProgress} size="small" />
                  </div>
                ) : (
                  <Upload.Dragger
                    accept=".wav,.mp3,.ogg,.m4a,.flac"
                    customRequest={handleUploadVoice}
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                    disabled={uploading}
                  >
                    <p className="ant-upload-drag-icon">
                      <Icon type="cloud-upload" />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag audio file to upload
                    </p>
                    <p className="ant-upload-hint">
                      WAV, MP3, OGG, M4A, FLAC (Max 50MB)
                    </p>
                  </Upload.Dragger>
                )}
              </div>
            )}
          </div>

          <div className="voiceover-step__divider" />

          {/* Section: System Voices */}
          <div className="voiceover-step__section">
            <div className="voiceover-step__section-header">
              <h3 className="voiceover-step__section-title">System Voices</h3>
              <Button
                type="link"
                icon="reload"
                onClick={fetchVoices}
                loading={loadingVoices}
                className="refresh-btn"
              >
                Refresh
              </Button>
            </div>

            {loadingVoices ? (
              <div className="voiceover-step__loading">
                <Spin size="default" />
              </div>
            ) : (
              <div className="voiceover-step__voice-grid">
                {voices
                  .filter(v => v.type !== 'uploaded')
                  .map((voice, index) => (
                    <VoiceCard
                      key={voice.id}
                      voice={voice}
                      isSelected={selectedVoice === voice.id}
                      isPlaying={playingVoice === voice.id}
                      progress={playingVoice === voice.id ? audioProgress : 0}
                      onSelect={() => handleSelectVoice(voice.id)}
                      onPlay={e => {
                        e.stopPropagation()
                        handlePlayPreview(voice.id)
                      }}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Voice Change Confirmation Modal */}
      <Modal
        visible={showConfirmationModal}
        onCancel={handleCancelChange}
        footer={null}
        centered
        width={520}
        className="voiceover-change-modal"
      >
        <div className="voiceover-change-modal-content">
          <div className="modal-icon-wrapper">
            <Icon type="warning" className="modal-warning-icon" />
          </div>
          <h3 className="modal-title">Existing Generated Content Detected</h3>
          <p className="modal-description">
            You have already generated content for this video. Changing the
            voice to <strong>"{getPendingVoiceName()}"</strong> will affect your
            existing work.
            <br />
            <br />
            Please choose how you would like to proceed:
          </p>
          <div className="modal-options">
            <div className="modal-option modal-option--recommended">
              <div className="option-icon option-icon--clone">
                <Icon type="copy" />
              </div>
              <div className="option-content">
                <h4 className="option-title">
                  Clone as New Video
                  <span className="recommended-badge">Recommended</span>
                </h4>
                <p className="option-description">
                  Safely creates a brand new copy of this video with the new
                  voice. Your current video and all generated content will be
                  saved and remain untouched.
                </p>
              </div>
            </div>
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="edit" />
              </div>
              <div className="option-content">
                <h4 className="option-title">Override Current Video</h4>
                <p className="option-description">
                  Updates this exact video with the new voice.
                  <span className="warning-text">
                    <Icon type="warning" /> Warning: Audio will be regenerated.
                    {hasGeneratedVideo &&
                      ' Videos with different audio duration will need to be regenerated.'}
                    Images will be preserved.
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <Button onClick={handleCancelChange} size="large">
              Cancel
            </Button>

            <div className="modal-actions-right">
              <Button
                type="default"
                onClick={handleOverride}
                size="large"
                icon="edit"
              >
                Override
              </Button>
              <Button
                type="primary"
                onClick={handleClone}
                size="large"
                icon="copy"
              >
                Clone
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// Sub-component for cleaner code
const VoiceCard = ({
  voice,
  isSelected,
  isPlaying,
  progress,
  onSelect,
  onPlay
}) => {
  const isUploaded = voice.type === 'uploaded'

  const buttonStyle = isPlaying
    ? {
        background: `conic-gradient(#5057d5 ${progress}%, #eef0ff ${progress}% 100%)`,
        padding: '2px', // Create space for the border
        backgroundClip: 'content-box, border-box'
      }
    : {}

  return (
    <div
      className={`voiceover-card ${
        isSelected ? 'voiceover-card--selected' : ''
      } ${isUploaded ? 'voiceover-card--uploaded' : ''}`}
      onClick={onSelect}
    >
      <div className="voiceover-card__visual">
        <Avatar
          size={40}
          icon={isUploaded ? 'user' : 'customer-service'}
          className={`voiceover-card__avatar ${
            isSelected ? 'voiceover-card__avatar--selected' : ''
          }`}
        />
        <div
          className={`voiceover-card__radio ${
            isSelected ? 'voiceover-card__radio--checked' : ''
          }`}
        >
          {isSelected && <div className="voiceover-card__radio-dot" />}
        </div>
      </div>

      <div className="voiceover-card__content">
        <span className="voiceover-card__name">{voice.name}</span>
        <span className="voiceover-card__type">
          {isUploaded ? 'Custom' : 'System'} Voice
        </span>
      </div>

      <div className="voiceover-card__actions">
        <div
          className={`voiceover-card__play-wrapper ${
            isPlaying ? 'voiceover-card__play-wrapper--playing' : ''
          }`}
          style={{
            position: 'relative',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            overflow: 'hidden'
          }}
        >
          {isPlaying && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `conic-gradient(#5057d5 ${progress}%, #eef0ff 0)`,
                zIndex: 0
              }}
            />
          )}
          <button
            className={`voiceover-card__play-btn ${
              isPlaying ? 'voiceover-card__play-btn--playing' : ''
            }`}
            onClick={onPlay}
            style={
              isPlaying
                ? {
                    position: 'absolute',
                    top: '3px',
                    left: '3px',
                    right: '3px',
                    bottom: '3px',
                    width: 'calc(100% - 6px)',
                    height: 'calc(100% - 6px)',
                    zIndex: 1,
                    borderRadius: '50%',
                    background: '#ffffff', // White background for premium contrast
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)' // Subtle shadow
                  }
                : {}
            }
          >
            <Icon
              type={isPlaying ? 'pause' : 'caret-right'}
              style={isPlaying ? { color: '#5057d5', fontSize: '14px' } : {}}
            />
          </button>
        </div>
      </div>
    </div>
  )
}

export default Voiceover
